// src/components/sku/SkuEditModal.tsx
import React, { useEffect, useMemo, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";

import type { SkuCreatePayload, SkuItem, SkuType } from "src/types/sku";

type Mode = "create" | "edit";

/**
 * ✅ 서버 스펙(수정용 body)에 맞춘 타입
 * - create에는 없고 edit에는 id가 필요
 */
export type SkuUpdatePayload = {
  id: number;
  skuCode: string;
  skuName: string;
  skuDescription: string;
  manufacturer: string;
  originCountry: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  barcode: string;
  modelNumber: string;
  skuCategory: string;
  isActive: boolean;
};

type Props =
  | {
      open: boolean;
      mode: "create";
      item?: null;
      onClose: () => void;
      /**
       * ✅ true => 성공(닫기), false => 실패(유지)
       */
      onSubmit: (payload: SkuCreatePayload) => Promise<boolean>;
    }
  | {
      open: boolean;
      mode: "edit";
      item: SkuItem;
      onClose: () => void;
      onSubmit: (payload: SkuUpdatePayload) => Promise<boolean>;
    };

const emptyForm = (): SkuCreatePayload => ({
  skuCode: "",
  skuName: "",
  skuDescription: "",
  skuType: "PRODUCT",
  barcode: "",
  modelNumber: "",
  skuCategory: "",
  manufacturer: "",
  originCountry: "",
  weight: 0,
  length: 0,
  width: 0,
  height: 0,
  isActive: true,
});

const toNumber = (v: string) => {
  if (v.trim() === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export default function SkuEditModal(props: Props) {
  const { open, mode, onClose } = props;
  const item = mode === "edit" ? props.item : null;

  const initial = useMemo(() => emptyForm(), []);
  const [form, setForm] = useState<SkuCreatePayload>(initial);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setErrorMsg(null);
    setSaving(false);

    if (mode === "create") {
      setForm(initial);
      return;
    }

    // edit
    if (item) {
      setForm({
        skuCode: item.skuCode ?? "", // 화면에 보여주려면 유지
        skuName: item.skuName ?? "",
        skuDescription: item.skuDescription ?? "",
        skuType: item.skuType ?? "PRODUCT", // 화면에 보여주려면 유지
        barcode: item.barcode ?? "",
        modelNumber: item.modelNumber ?? "",
        skuCategory: item.skuCategory ?? "",
        manufacturer: item.manufacturer ?? "",
        originCountry: item.originCountry ?? "",
        weight: item.weight ?? 0,
        length: item.length ?? 0,
        width: item.width ?? 0,
        height: item.height ?? 0,
        isActive: !!item.isActive,
      });
    } else {
      setForm(initial);
    }
  }, [open, mode, item, initial]);

  const setField = <K extends keyof SkuCreatePayload>(key: K, value: SkuCreatePayload[K]) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const validate = () => {
    if (mode === "create") {
      if (!form.skuCode.trim()) return "skuCode는 필수입니다.";
      if (!form.skuName.trim()) return "skuName은 필수입니다.";
      if (!form.skuType) return "skuType은 필수입니다.";
      return null;
    }

    // edit: 서버 스펙상 skuName만 필수로 가정(필요하면 추가)
    if (!form.skuName.trim()) return "skuName은 필수입니다.";
    if (!item?.id) return "수정 대상 id가 없습니다.";
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setErrorMsg(v);
      return;
    }

    setSaving(true);
    setErrorMsg(null);

    try {
      if (mode === "create") {
        const payload: SkuCreatePayload = {
          ...form,
          skuCode: form.skuCode.trim(),
          skuName: form.skuName.trim(),
          skuDescription: form.skuDescription?.trim() || "",
          barcode: form.barcode?.trim() || "",
          modelNumber: form.modelNumber?.trim() || "",
          skuCategory: form.skuCategory?.trim() || "",
          manufacturer: form.manufacturer?.trim() || "",
          originCountry: form.originCountry?.trim() || "",
          weight: Number(form.weight || 0),
          length: Number(form.length || 0),
          width: Number(form.width || 0),
          height: Number(form.height || 0),
          isActive: !!form.isActive,
        };

        const ok = await props.onSubmit(payload);
        if (ok) onClose();
        return;
      }

      // mode === "edit"
      const payload: SkuUpdatePayload = {
        id: item!.id,
        skuCode: form.skuCode.trim(),
        skuName: form.skuName.trim(),
        skuDescription: (form.skuDescription ?? "").trim(),
        manufacturer: (form.manufacturer ?? "").trim(),
        originCountry: (form.originCountry ?? "").trim(),
        weight: Number(form.weight || 0),
        length: Number(form.length || 0),
        width: Number(form.width || 0),
        height: Number(form.height || 0),
        barcode: (form.barcode ?? "").trim(),
        modelNumber: (form.modelNumber ?? "").trim(),
        skuCategory: (form.skuCategory ?? "").trim(),
        isActive: !!form.isActive,
      };

      const ok = await props.onSubmit(payload);
      if (ok) onClose();
    } catch (e: any) {
      setErrorMsg(e?.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "create" ? "SKU 등록" : "SKU 수정";

  // ✅ backdrop 클릭 / ESC 닫기 방지
  const handleDialogClose = (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
    if (saving) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 900 }}>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              label="skuCode *"
              value={form.skuCode}
              onChange={(e) => setField("skuCode", e.target.value)}
              sx={{ minWidth: 220 }}
              // disabled={mode === "edit"} // ✅ 수정 시 skuCode 변경 금지
            />
            <TextField
              size="small"
              label="skuName *"
              value={form.skuName}
              onChange={(e) => setField("skuName", e.target.value)}
              sx={{ minWidth: 260, flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="skuType-label">skuType *</InputLabel>
              <Select
                labelId="skuType-label"
                label="skuType *"
                value={form.skuType}
                onChange={(e) => setField("skuType", e.target.value as SkuType)}
                disabled={mode === "edit"} // ✅ 수정 시 skuType 변경 금지(서버 스펙에 없으니)
              >
                <MenuItem value="PRODUCT">PRODUCT</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TextField
            size="small"
            label="skuDescription"
            value={form.skuDescription || ""}
            onChange={(e) => setField("skuDescription", e.target.value)}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ sx: { "& textarea": { resize: "vertical" } } }}
          />

          <Divider />

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              label="barcode"
              value={form.barcode || ""}
              onChange={(e) => setField("barcode", e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              label="modelNumber"
              value={form.modelNumber || ""}
              onChange={(e) => setField("modelNumber", e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              label="skuCategory"
              value={form.skuCategory || ""}
              onChange={(e) => setField("skuCategory", e.target.value)}
              sx={{ minWidth: 220 }}
            />
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              label="manufacturer"
              value={form.manufacturer || ""}
              onChange={(e) => setField("manufacturer", e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              label="originCountry"
              value={form.originCountry || ""}
              onChange={(e) => setField("originCountry", e.target.value)}
              sx={{ minWidth: 220 }}
            />
          </Stack>

          <Divider />

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              label="weight"
              type="number"
              value={String(form.weight ?? 0)}
              onChange={(e) => setField("weight", toNumber(e.target.value))}
              sx={{ width: 180 }}
            />
            <TextField
              size="small"
              label="length"
              type="number"
              value={String(form.length ?? 0)}
              onChange={(e) => setField("length", toNumber(e.target.value))}
              sx={{ width: 180 }}
            />
            <TextField
              size="small"
              label="width"
              type="number"
              value={String(form.width ?? 0)}
              onChange={(e) => setField("width", toNumber(e.target.value))}
              sx={{ width: 180 }}
            />
            <TextField
              size="small"
              label="height"
              type="number"
              value={String(form.height ?? 0)}
              onChange={(e) => setField("height", toNumber(e.target.value))}
              sx={{ width: 180 }}
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={!!form.isActive}
                onChange={(e) => setField("isActive", e.target.checked)}
              />
            }
            label="활성 여부"
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          닫기
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "저장 중..." : "저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
